// BOORO: what an approval will do. A mail looks like a mail, not like a tool call.

import { Trans, useLingui } from "@lingui/react/macro";
import type { ApprovalDetail, ApprovalPreview } from "@rakazo/contracts";
import { Button, Input, Textarea } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

/** The fields of a preview a person may correct before allowing. */
function useEditableFields(preview: ApprovalPreview): { key: string; label: string }[] {
  const { t } = useLingui();
  if (preview.kind === "email") {
    return [
      { key: "to", label: t`To` },
      { key: "cc", label: t`Cc` },
      { key: "subject", label: t`Subject` },
      { key: "body", label: t`Text` },
    ];
  }
  if (preview.kind === "calendar") {
    return [
      { key: "title", label: t`Title` },
      { key: "start", label: t`Start` },
      { key: "end", label: t`End` },
    ];
  }
  if (preview.kind === "payment") {
    return [
      { key: "amount", label: t`Amount` },
      { key: "currency", label: t`Currency` },
      { key: "recipient", label: t`Recipient` },
    ];
  }
  if (preview.kind === "page") {
    return [
      { key: "title", label: t`Title` },
      { key: "content", label: t`Text` },
    ];
  }
  return [];
}

function currentValue(preview: ApprovalPreview, key: string): string {
  if (preview.kind === "email") return preview[key as "to" | "cc" | "subject" | "body"] ?? "";
  if (preview.kind === "calendar") {
    return preview[key as "title" | "start" | "end"] ?? "";
  }
  if (preview.kind === "payment") {
    return preview[key as "amount" | "currency" | "recipient"] ?? "";
  }
  if (preview.kind === "page") return preview[key as "title" | "content"] ?? "";
  const field = preview.fields.find((entry) => entry.label === key);
  return field?.value ?? "";
}

export function ApprovalPreviewView({
  detail,
  canChange,
  onAllow,
}: {
  detail: ApprovalDetail;
  /** Whether the approval is still open, so its values may be changed. */
  canChange: boolean;
  /** Allows the action after the changes were saved. */
  onAllow?: (answer: string) => Promise<void>;
}) {
  const { t } = useLingui();
  const [editing, setEditing] = useState(false);
  const [changes, setChanges] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fields = useEditableFields(detail.preview);

  useEffect(() => {
    setEditing(false);
    setChanges({});
    setError("");
  }, [detail.effectId]);

  async function approveWithChanges() {
    setBusy(true);
    setError("");
    try {
      await rpc.booroApprovals.approveWithChanges({ effectId: detail.effectId, changes });
      // The changed values are stored; the card still has to allow the action.
      await onAllow?.("allow");
    } catch (cause) {
      setError(errorText(cause, t`Could not approve this action`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="approval-preview"
      className="mt-3 rounded-xl border border-border bg-muted/40 p-3"
    >
      {detail.task ? (
        <p className="mb-2 text-[12.5px] text-muted-foreground" dir="auto">
          {t`For task ${detail.task.ref} · ${detail.task.title}`}
        </p>
      ) : null}
      {detail.preview.kind === "email" ? (
        <div className="space-y-1.5 text-[13.5px]">
          <p dir="auto">
            <span className="text-muted-foreground">{t`To`}: </span>
            {detail.preview.to || "-"}
          </p>
          {detail.preview.cc ? (
            <p dir="auto">
              <span className="text-muted-foreground">{t`Cc`}: </span>
              {detail.preview.cc}
            </p>
          ) : null}
          <p className="font-medium" dir="auto">
            {detail.preview.subject || "-"}
          </p>
          {detail.preview.body ? (
            <p className="whitespace-pre-wrap break-words text-foreground/85" dir="auto">
              {detail.preview.body}
            </p>
          ) : null}
        </div>
      ) : detail.preview.kind === "page" ? (
        <div className="space-y-1.5 text-[13.5px]">
          <p className="text-[12.5px] text-muted-foreground" dir="auto">
            {detail.preview.path
              ? t`Changes the handbook page ${detail.preview.path}`
              : t`New page in the handbook`}
            {detail.preview.office ? ` · ${detail.preview.office}` : ""}
          </p>
          <p className="font-medium" dir="auto">
            {detail.preview.title || detail.preview.path || "-"}
          </p>
          <p
            data-testid="approval-page-content"
            className="max-h-80 overflow-y-auto whitespace-pre-wrap break-words text-foreground/85"
            dir="auto"
          >
            {detail.preview.content}
          </p>
        </div>
      ) : detail.preview.kind === "generic" ? (
        <dl className="space-y-1 text-[13.5px]">
          {detail.preview.fields.map((field) => (
            <div key={field.label} className="flex gap-2" dir="auto">
              <dt className="shrink-0 text-muted-foreground">{field.label}</dt>
              <dd className="min-w-0 break-words">{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : detail.preview.kind === "calendar" ? (
        <dl className="space-y-1 text-[13.5px]">
          <div className="flex gap-2" dir="auto">
            <dt className="shrink-0 text-muted-foreground">{t`Title`}</dt>
            <dd className="min-w-0 break-words">{detail.preview.title || "-"}</dd>
          </div>
          <div className="flex gap-2" dir="auto">
            <dt className="shrink-0 text-muted-foreground">{t`When`}</dt>
            <dd className="min-w-0 break-words">
              {detail.preview.start}
              {detail.preview.end ? ` – ${detail.preview.end}` : ""}
            </dd>
          </div>
          {detail.preview.attendees.length > 0 ? (
            <div className="flex gap-2" dir="auto">
              <dt className="shrink-0 text-muted-foreground">{t`Attendees`}</dt>
              <dd className="min-w-0 break-words">{detail.preview.attendees.join(", ")}</dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <dl className="space-y-1 text-[13.5px]">
          <div className="flex gap-2" dir="auto">
            <dt className="shrink-0 text-muted-foreground">{t`Amount`}</dt>
            <dd className="min-w-0 break-words">
              {detail.preview.amount} {detail.preview.currency}
            </dd>
          </div>
          <div className="flex gap-2" dir="auto">
            <dt className="shrink-0 text-muted-foreground">{t`Recipient`}</dt>
            <dd className="min-w-0 break-words">{detail.preview.recipient || "-"}</dd>
          </div>
        </dl>
      )}

      {canChange && fields.length > 0 ? (
        <div className="mt-3">
          {editing ? (
            <div className="space-y-2">
              {fields.map((field) => (
                <div key={field.key} className="space-y-1">
                  <label
                    className="text-[12.5px] text-muted-foreground"
                    htmlFor={`preview-${field.key}`}
                  >
                    {field.label}
                  </label>
                  {field.key === "body" || field.key === "content" ? (
                    <Textarea
                      id={`preview-${field.key}`}
                      rows={field.key === "content" ? 12 : 4}
                      value={changes[field.key] ?? currentValue(detail.preview, field.key)}
                      onChange={(event) =>
                        setChanges((previous) => ({ ...previous, [field.key]: event.target.value }))
                      }
                    />
                  ) : (
                    <Input
                      id={`preview-${field.key}`}
                      value={changes[field.key] ?? currentValue(detail.preview, field.key)}
                      onChange={(event) =>
                        setChanges((previous) => ({ ...previous, [field.key]: event.target.value }))
                      }
                    />
                  )}
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" disabled={busy} onClick={() => void approveWithChanges()}>
                  <Trans>Approve with changes</Trans>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  <Trans>Cancel</Trans>
                </Button>
              </div>
              {error ? (
                <p role="alert" className="text-[13px] text-destructive">
                  {error}
                </p>
              ) : null}
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Trans>Change before approving</Trans>
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
